import React, { useMemo } from 'react';
import { useQuery } from 'utils/react-query';
import classnames from 'classnames';

import LinkButton from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';
import Loader from 'components/uiLibrary/Loader';
import EntityCard from 'components/Globals/EntityCard';

import queries from 'containers/Artist/queries';

import { TP, ENTITY_TYPE, ARTIST_IMPRESSION_TRACKING_EVENTS } from 'constants/index';

import usePageContext from 'utils/hooks/usePageContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';

import { useTranslation } from 'src/i18n';

import classes from './FeaturedArtists.module.scss';

const ArtistCard = ({ artist, large, preload, openNewTab }) => {
  const { height, width, typographyProps } = useMemo(
    () => ({
      height: large ? 259 : 148,
      width: large ? 175 : 100,
      typographyProps: {
        name: {
          size: large ? 14 : 11,
        },
        subtext: {
          size: large ? 12 : 9,
        },
      },
    }),
    [large],
  );

  if (!artist) {
    return null;
  }

  return (
    <EntityCard
      entityType={ENTITY_TYPE.ARTIST}
      entity={artist}
      height={height}
      width={width}
      preload={preload}
      typographyProps={typographyProps}
      eventType={ARTIST_IMPRESSION_TRACKING_EVENTS.ARTIST_CARD_IMPRESSION}
      featured
      openNewTab={openNewTab}
    />
  );
};

const FeaturedArtists = ({ filters = {}, limit, large = false, showMoreLink = false, openNewTab = false }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isMobile } = useDeviceTypeLayouts();
  const { navigate } = usePageContext();
  const { data, isLoading } = useQuery(
    queries.getArtists({
      filters: {
        sort: 'random',
        tier: 'A',
        ...filters,
      },
      limit,
    }),
  );

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {data?.data?.map((artist, index) => (
        <ArtistCard
          key={artist?.id}
          artist={artist}
          large={large}
          preload={isMobile ? false : index < 10}
          openNewTab={openNewTab}
        />
      ))}
      {!isLoading && showMoreLink && (
        <LinkButton
          styles={{
            root: classnames(classes.artistCard, {
              [classes.large]: large,
            }),
          }}
          {...navigate.getLinkProps({ entityType: ENTITY_TYPE.ARTIST, onlyLinkProps: true })}
          variant="native"
          isLink
        >
          <div className={classes.moreCard}>
            <Typography color="link" weight="bold">
              {t(`${TP}.FN_VIEW_MORE`)}
            </Typography>
          </div>
        </LinkButton>
      )}
    </div>
  );
};

export default FeaturedArtists;
